import React, { useState } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InvestorDocumentTypeService, type InvestorDocumentType } from '@/services/investorDocumentTypeService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ManageDocumentTypesModal', 'investor-documents');

interface ManageDocumentTypesModalProps {
  organizationId: string;
  types: InvestorDocumentType[];
  onClose: () => void;
  /** Called after a successful add/remove so the parent can reload the catalog. */
  onChanged: () => void;
}

/**
 * Item 11 (admin-only): create/remove custom Document Types. Built-ins (is_custom=false) are
 * shown but not deletable; a custom type in use by ≥1 document is blocked from deletion.
 */
export const ManageDocumentTypesModal: React.FC<ManageDocumentTypesModalProps> = ({
  organizationId, types, onClose, onChanged
}) => {
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setError('Enter a type name');
      return;
    }
    if (types.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      setError('This document type already exists');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      await InvestorDocumentTypeService.create(organizationId, trimmed);
      setNewName('');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add type');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (type: InvestorDocumentType) => {
    setDeletingId(type.id);
    setError(null);
    try {
      const count = await InvestorDocumentTypeService.usageCount(type.name);
      if (count > 0) {
        toast.error(`In use by ${count} document(s). Reassign or remove those documents first.`);
        return;
      }
      await InvestorDocumentTypeService.remove(type.id);
      onChanged();
    } catch (e) {
      log.error('Failed to delete document type', { id: type.id }, e as Error);
      toast.error(e instanceof Error ? e.message : 'Failed to delete type');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Manage Document Types</h3>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-3 flex gap-2">
            <input
              value={newName}
              onChange={(e) => {
 setNewName(e.target.value); setError(null); 
}}
              onKeyDown={(e) => {
 if (e.key === 'Enter') {
 void handleAdd(); 
} 
}}
              placeholder="New document type"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={() => void handleAdd()}
              disabled={adding}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </button>
          </div>
          {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {types.map((type) => (
              <div key={type.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
                <span className="text-foreground">
                  {type.name}
                  {!type.isCustom && <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">built-in</span>}
                </span>
                {type.isCustom && (
                  <button
                    onClick={() => void handleDelete(type)}
                    disabled={deletingId === type.id}
                    aria-label={`Delete ${type.name}`}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                  >
                    {deletingId === type.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
